import { WavStreamPlayer } from "./wav-stream-player";

interface TTSAudioBufferProps {
  onAudioEnded: (() => void) | null;
  showToast: (message: string) => void;
}

const BUFFER_SIZE_THRESHOLD_TO_START_PLAYING = 64000;

export class TTSAudioBuffer {
  public bufferedData: number[] = [];
  private lastOffset = 0;
  private onAudioEnded: (() => void) | null = null;
  private startedPlayingAudio = false;
  private hasAudioEnded = false;
  private showToast: (message: string) => void;
  private wavStreamPlayer: WavStreamPlayer | null = null;
  public hasTTSSlowConnectionToastShown = false;

  private initializeWavStreamPlayer() {
    this.wavStreamPlayer = new WavStreamPlayer({ sampleRate: 48000 });
    this.wavStreamPlayer.setOnEndedCallback(() => {
      this.reset(true);
    });
  }

  constructor({ onAudioEnded, showToast }: TTSAudioBufferProps) {
    this.onAudioEnded = onAudioEnded;
    this.showToast = showToast;
    this.initializeWavStreamPlayer();
  }

  // this must be called after user interaction with the app
  public async connectAudioContext() {
    await this.wavStreamPlayer?.connect();
  }

  private processBufferedDataIntoAudioSamples() {
    // audio samples array for 16 bit PCM audio
    const int16Array = new Int16Array(
      (this.bufferedData.length - this.lastOffset) / 2,
    );
    let j = 0;
    for (let i = this.lastOffset; i < this.bufferedData.length; i += 2) {
      if (i + 1 < this.bufferedData.length) {
        const aBit = this.bufferedData[i];
        const bBit = this.bufferedData[i + 1];
        const sample = (bBit << 8) | aBit;
        int16Array[j++] = sample;
        this.lastOffset = i + 2;
      }
    }

    this.wavStreamPlayer?.add16BitPCM(int16Array);
    this.startedPlayingAudio = true;
  }

  public addChunk(data: Uint8Array) {
    const stringRep = Array.from(data)
      .map((b) => String.fromCharCode(b))
      .join("");

    const hasAudioFileHeader = stringRep.includes("RIFF");

    if (hasAudioFileHeader) {
      const wavData = data.slice(44);
      this.bufferedData.push(...Array.from(wavData));
    } else {
      this.bufferedData.push(...Array.from(data));
    }

    if (
      this.bufferedData.length > this.lastOffset &&
      (this.startedPlayingAudio ||
        this.bufferedData.length > BUFFER_SIZE_THRESHOLD_TO_START_PLAYING)
    ) {
      this.processBufferedDataIntoAudioSamples();
    }
  }

  // TODO: add flush method to send all remaining data from the buffer to the wav stream player
  // and call this in chat tts request on ended
  public flushBufferedData() {
    this.processBufferedDataIntoAudioSamples();
  }

  public async reset(showToast: boolean) {
    if (showToast && !this.hasAudioEnded) {
      this.showToast("Poor network connection. Expect audio degradation.");
    }
    this.bufferedData = [];
    this.lastOffset = 0;
    this.startedPlayingAudio = false;
    await this.wavStreamPlayer?.interrupt();
    if (this.hasAudioEnded) {
      this.onAudioEnded?.();
      this.hasAudioEnded = false;
    }
    this.initializeWavStreamPlayer();
    await this.wavStreamPlayer?.connect();
  }

  public setHasAudioEnded(hasAudioEnded: boolean) {
    this.hasAudioEnded = hasAudioEnded;
  }

  public setHasTTSSlowConnectionToastShown(hasToastShown: boolean) {
    this.hasTTSSlowConnectionToastShown = hasToastShown;
  }
}
